// Never import any server-only code here, this is also used on the client side...
import type { TranslationsMap } from './types';

// English version of the translation file
export const translations: TranslationsMap = {
  emails: {
    footer: {
      kindRegards: 'With kind regards,',
      questionsOrProblems: 'Questions or problems?',
      contactUs: 'Contact us',
    },
    transportOrder: {
      confirm: 'Confirm',
      refuse: 'Refuse',
    },
    forgotPassword: {
      resetLink: 'Configure new password',
    },
    userInvite: {
      view: 'View invitation',
    },
    attachments: {
      quotation: 'Quotation {{reference}}',
    },
    customerComplaintAlert: {
      order: 'View order',
    },
  },
  truckDocumentType: {
    Insurance: 'Insurance',
    Examination: 'Examination',
  },
  orderDocumentType: {
    cmr: 'CMR',
    deliveryNote: 'Delivery note',
    weighSlip: 'Weigh slip',
    palletNote: 'Pallet note',
    annexII: 'Annex II',
    t1Document: 'T1 document',
    annexVII: 'Annex VII',
    saleInvoice: 'Invoice',
    transportOrder: 'Transport order',
    orderConfirmation: 'Order confirmation',
    sustainabilityReport: 'Sustainability report',
    customerAttachment: 'Customer attachment',
    planning: 'Planning',
    other: 'Other',
  },
  documentRejection: {
    subject: 'Document rejected for order {{orderNumber}}',
    intro: 'We were unable to approve the {{documentType}} document we received for order {{orderNumber}}. Reasons:',
    outro: 'Please re-submit a corrected document via your usual channel or via the carrier portal. Thank you!',
    MissingSignature: "The receiver's signature is missing on the CMR.",
    WrongCustomer: "The customer on the document does not match this order's customer.",
    WrongOrgOnDocument: 'The forwarder/sender on the document does not match our organisation.',
    NoOrderReference: 'We could not find a valid order or invoice reference linking this document to an order.',
    WrongDocumentType: 'The document type does not match what we expect for this order.',
    LowQuality:
      'The scan/photo quality is too low to reliably validate the content. Please re-send a higher-quality copy.',
  },
  trailerTypes: {
    BoxTrailer: 'Box Trailer',
    FlatBed: 'Flatbed',
    Mega: 'Mega Trailer',
    Refrigerated: 'Refrigerated',
    RoadTrain: 'Road Train',
    Tautliner: 'Tautliner',
    Joloda: 'Tautliner / Joloda',
    TautlinerCoil: 'Tautliner / Coil',
  },
  loadingMethods: {
    Back: 'Back',
    Side: 'Side',
    Top: 'Via the roof',
  },
  documents: {
    note: {
      extraInformation: 'Extra information',
    },
    letter: {
      letter: 'Letter',
    },
    paymentReminder: {
      invoiceNumber: 'Invoice number',
      invoiceDate: 'Invoice date',
      dueDate: 'Due date',
      amount: 'Amount',
      expiredAmount: 'Expired amount',
      currency: 'Currency',
      totalOutstanding: 'Total outstanding',
      salutation: 'Dear Sir or Madam,',
      subject: {
        first: 'Subject: payment reminder — {{reference}}',
        second: 'Subject: second payment reminder — {{reference}}',
        third: 'Subject: third payment reminder — {{reference}}',
        final: 'Subject: notice of default — {{reference}}',
      },
      intro: {
        first:
          'Our records show that the invoices below have not yet been settled. If your payment has crossed with this letter, please disregard it.',
        second: 'Despite our earlier reminder, we have not yet received payment for the invoices below:',
        third: 'Despite repeated reminders, the invoices below are still outstanding:',
        final: 'Despite earlier payment reminders, we have to date received no payment for the invoices below:',
      },
      request:
        'We request that you transfer the outstanding amount of {{amount}} to the account below within {{days}} calendar days of receiving this letter, quoting the reference:',
      acknowledgement:
        'If you do not pay and do not state a reason for this, it will be regarded as an acknowledgement of the debt.',
      escalation: {
        first: 'Should we not receive your payment within this period, we will send you a further reminder.',
        second: 'Should we not receive your payment within this period, we will be obliged to escalate this file.',
        third:
          'Should payment not be made within this period, this file will be transferred to our debt collection procedure.',
        final:
          'Should payment not be made within this period, we will hand this file to our legal counsel without further notice, with instructions to start legal proceedings against you. The costs of this will be entirely at your expense.',
      },
      closing: {
        first: 'We trust you will settle the outstanding amount within the requested period.',
        second: 'We trust you will settle the outstanding amount within the requested period.',
        third: 'We trust you will settle the outstanding amount within the requested period.',
        final:
          'We trust this will not be necessary and that you will settle the outstanding amount within the requested period.',
      },
      sincerely: 'Sincerely,',
      signature: 'Accounting {{company}}',
      account: 'Account {{company}}',
      accounts: 'Accounts {{company}}',
      reference: 'Reference',
      amountDue: 'Amount due',
      bic: 'BIC',
      heldBy: 'held by {{company}}',
      date: 'Date',
      ourReference: 'Our reference',
      customerNumber: 'Customer number',
      yourVatNumber: 'Your VAT number',
      contact: 'Contact',
      phone: 'Phone',
    },
    quotation: {
      quotation: 'Quotation',
      customerNumber: 'Customer number',
      reference: 'Reference',
      date: 'Quotation date',
      dueDate: 'Expiration date',
      from: 'From',
      to: 'To',
      volume: 'Volume',
      price: 'Price',
      generalTermsAndConditions: 'General sales conditions attached',
      questionsOrComments: 'For questions contact us via email on {{email}}',
      trailerTypes: 'Trailer types',
    },
    transportOrder: {
      trailerTypes: 'Trailer Types',
      loadingMethod: 'Loading Method',
      transportOrder: 'Transport Order',
      date: 'Date',
      orderRef: 'Order Reference',
      load: 'Load',
      unload: 'Unload',
      reference: 'Reference',
      goods: 'Goods',
      description: 'Description',
      packageType: 'Pallet type',
      palletCount: 'Pallets',
      weight: 'Weight',
      LoadingMeter: 'Loadmeter',
      freightRate: 'Freight rate',
      price: 'Price',
      vat: 'VAT',
      questionsOrComments: 'Questions or comments?',
      contactFileManager: 'Feel free to contact your file manager',
      anonymousCmr: 'ANONYMOUS CMR MUST BE CREATED FOR THIS ORDER.',
      phone: 'Phone',
      email: 'E-mail',
    },
    planning: {
      planning: 'Planning',
      licensePlate: 'License plate',
      codeName: 'Codename',
    },
    kilometers: {
      full: 'Loaded kilometers',
      empty: 'Empty kilometers',
      total: 'Total kilometers',
      planningTotal: 'Planning total',
    },
    orderConfirmation: {
      orderConfirmation: 'Order Confirmation ',
      dearCustomer: 'Dear customer,',
      herebyWeConfirmYourOrder: 'We hereby confirm your order for the following transport:',
      date: 'Date',
      ourReference: 'Our reference',
      yourReference: 'Your reference',
      load: 'Load',
      unload: 'Unload',
      reference: 'Reference',
      goods: 'Goods',
      description: 'Description',
      packageType: 'Pallet type',
      palletCount: 'Pallets',
      weight: 'Weight',
      LoadingMeter: 'Loadmeter',
      freightRate: 'Freight rate',
      price: 'Price',
      vat: 'VAT',
      questionsOrComments: 'Questions or comments?',
      contactFileManager: 'Feel free to contact your file manager',
      phone: 'Phone',
      email: 'E-mail',
    },
    orderCancellation: {
      orderCancellation: 'Order cancellation',
      dearCustomer: 'Dear customer,',
      orderCancelledText: 'Your order for the transport below has hereby been cancelled:',
      date: 'Date',
      ourReference: 'Our reference',
      yourReference: 'Your reference',
      load: 'Load',
      unload: 'Unload',
      reference: 'Reference',
      goods: 'Goods',
      description: 'Description',
      packageType: 'Pallet type',
      palletCount: 'Pallets',
      weight: 'Weight',
      LoadingMeter: 'Loadmeter',
      freightRate: 'Freight rate',
      price: 'Price',
      vat: 'VAT',
      questionsOrComments: 'Questions or comments?',
      contactFileManager: 'Feel free to contact your file manager',
      phone: 'Phone',
      email: 'E-mail',
    },
    invoice: {
      invoice: 'Invoice',
      customerNumber: 'Customer number',
      invoiceNumber: 'Invoice number',
      invoiceDate: 'Invoice date',
      dueDate: 'Due date',
      load: 'Load',
      unload: 'Unload',
      ourReference: 'Our reference',
      yourReference: 'Your reference',
      description: 'Description',
      quantity: 'Amount',
      unitPrice: 'Unitprice',
      total: 'Total',
      vat: 'VAT',
      paymentInformation: 'Payment Information',
      amountToPay: 'Amount to be paid',
      toBePaidBefore: 'To be paid before',
      beneficiary: 'Beneficiary',
      message: 'Message',
      generalTermsAndConditions: 'General sales conditions attached',
      questionsOrComments: 'For any questions, please contact accounting at {{email}}',
    },
    sustainabilityReport: {
      sustainabilityReport: 'Sustainability Report',
      date: 'Date',
      ourReference: 'Our Reference',
      yourReference: 'Your Reference',
      truck: 'Truck',
      distance: 'Distance',
      weight: 'Weight',
      emissions: 'Emissions',
      questionsOrComments: 'Questions or remarks?',
      contactFileManager: 'Feel free to contact your file manager',
      phone: 'Phone',
      email: 'E-mail',
    },
    globalSustainabilityReport: {
      sustainabilityReport: 'Sustainability Report',
      date: 'Date',
      startDate: 'Start date',
      endDate: 'End date',
      orderCount: 'Number of Orders',
      truck: 'Truck',
      totals: 'Total',
      distance: 'Distance',
      weight: 'Weight',
      emissions: 'CO2 emissions',
      averagePerOrder: 'Average per order',
      framework: 'For details about the emissions calculation, please check the sustainability policy on our website.',
      questionsOrComments: 'Questions or comments?',
      contactFileManager: 'Feel free to contact your file manager',
      phone: 'Tel.',
      email: 'Email',
    },
    rideOverview: {
      title: 'Overview',
      entryNumber: 'Overview reference',
      dossier: 'File',
      draftBanner: 'Draft — not yet approved',
      invoiceRequestNotice:
        'This document is not a valid self-billing invoice. Upon receiving this overview, {{customer}} expects an invoice from you for these orders.',
    },
    selfBill: {
      title: 'Self-billing invoice',
      number: 'No.',
      draftBanner: 'Draft — not yet approved',
      supplierLabel: 'Supplier — carrier · we pay this company',
      customerLabel: 'Customer — draws up this invoice on behalf of the supplier',
      vatCompanyNumber: 'VAT / Company No.:',
      vatShort: 'VAT',
      carrierNumber: 'Carrier number: {{number}}',
      customerRegister: 'RLE Gent, division Kortrijk',
      bankLabel: "Payment to the supplier's bank account",
      bankNotice:
        'Registered in our carrier file. If this is not your account, contact {{email}} immediately. Bank details are only changed through our verification procedure, never on the basis of an e-mail request.',
      documentType: 'Document type',
      invoiceNumber: 'Invoice number',
      invoiceDate: 'Invoice date',
      servicePeriod: 'Service period',
      agreement: 'Agreement',
      paymentDate: 'Payment date',
      natureOfServices:
        'Nature of the services: {{nature}}, one trip per line, carried out on the shipment date shown. Unit price = line amount (quantity 1), excluding VAT.',
      roadFreightTransport: 'road freight transport',
      columnNumber: 'No.',
      columnFile: 'File',
      columnShipmentDate: 'Shipment date',
      columnLoading: 'Loading',
      columnUnloading: 'Unloading',
      columnQuantity: 'Qty',
      columnVat: 'VAT',
      columnAmount: 'EUR',
      tripCountOne: '1 trip on this invoice',
      tripCountOther: '{{count}} trips on this invoice',
      totalServices: 'Total transport services (excl. VAT)',
      earlyPaymentDiscount: 'Early payment discount {{percentage}}',
      earlyPaymentCondition:
        'For payment within 7 days, a discount of {{percentage}} ({{amount}}) is deducted. To pay: {{withDiscount}}. After this term: {{withoutDiscount}}.',
      taxableAmount: 'Taxable amount at {{rate}}',
      vat: 'VAT {{rate}}',
      totalToPay: 'Total to pay',
      paymentNotice:
        '{{amount}} will be transferred automatically on {{date}} to {{iban}}. No action is required from your side.',
      paymentNoticeWithoutAccount:
        '{{amount}} will be transferred automatically on {{date}}. No action is required from your side.',
      yourInvoiceTitle: 'This is your invoice. Please do not send us an invoice for these trips.',
      yourInvoice:
        'This document is issued by {{customer}} in the name and on behalf of {{supplier}} under the self-billing agreement between both parties. It is not a proposal. An invoice you send yourself for the same trips will be rejected as a duplicate.',
      acceptanceTitle: 'Acceptance',
      acceptance:
        'This invoice is deemed accepted by the supplier unless it is contested in writing at {{email}} within 7 days of the invoice date. Corrections are made by a self-billed credit note.',
      paymentTermTitle: 'Payment term',
      paymentTerm:
        'The payment term is the one agreed in your contract. Where a discount was granted, the payment term is 7 days.',
      missingTripTitle: 'Missing a trip?',
      missingTrip:
        'A trip is not on this invoice because its documents are not in order. Upload the correct documents in the carrier portal and the trip is included on your next self-billing invoice.',
      questionsTitle: 'Questions?',
      questions: 'Contact {{email}}, quoting the invoice number.',
      legalSelfBilling: 'Self-billing',
      legalNotice:
        '{{selfBilling}} — invoice issued by the customer in the name and on behalf of the supplier (art. 224 and 226 (10a) Directive 2006/112/EC) · Self-billing agreement {{agreement}} · Peppol BIS Self-Billing 3.0, document type 389',
      legalTerms:
        'General sales conditions attached and available at {{website}} · Belgian law applies · Courts of Kortrijk',
      pageFooter: '{{title}} {{number}} · {{company}}',
      page: 'Page {{page}} of {{total}}',
    },
    creditNote: {
      creditNote: 'Credit note',
      customerNumber: 'Customer number',
      creditNoteNumber: 'Credit note number',
      date: 'Date',
      dueDate: 'Due date',
      load: 'Load',
      unload: 'Unload',
      ourReference: 'Our reference',
      yourReference: 'Your reference',
      description: 'Description',
      quantity: 'Amount',
      unitPrice: 'Unitprice',
      total: 'Total',
      vat: 'VAT',
      paymentInformation: 'Payment Information',
      amountToPay: 'Amount to be paid',
      toBePaidBefore: 'To be paid before',
      payer: 'Payer',
      message: 'Message',
      generalTermsAndConditions: 'General sales conditions attached',
      questionsOrComments: 'For any questions, please contact accounting at {{email}}',
    },
    carrierTruckReport: {
      title: 'Truck performance report',
      truckname: 'Name',
      totalIncome: 'Total income',
      totalKm: 'Total kilometers',
    },
    totals: {
      totalExclVat: 'Total excl. VAT',
      totalVat: 'Total VAT',
      totalInclVat: 'Total incl. VAT',
      cashDiscount: 'Early payment discount',
      netAmountToPay: 'Net amount to pay',
      vatReverseCharged: 'VAT Reverse Charge, article 44 VAT Directive',
    },
  },
  carrierTerms: {
    agreement: 'I agree to the {{terms}}, including the self-billing procedure.',
    termsLink: 'general terms and conditions',
  },
  pages: {
    confirmOrderStates: {
      headingRefused: 'Order has been declined',
      headingCancelled: 'Order has been cancelled',
      headingConfirmed: 'Order has been confirmed',
      headingUnknownStatus: 'Order has already been executed or status unknown',
    },
    confirmOrderPlanningStates: {
      headingRefused: 'Planning has been declined',
      headingCancelled: 'Planning has been cancelled',
      headingConfirmed: 'Planning has been confirmed',
      headingUnknownStatus: 'Planning has already been executed or status unknown',
    },
    confirmOrder: {
      heading: 'Confirm transport order {{order.orderNumber}}',
      orderConfirmed: 'Order confirmed',
      orderConfirmationError: 'Could not confirm order: {{error}}',
      confirmButton: `Confirm transport order`,
    },
    confirmPlanning: {
      heading: 'Confirm planning {{planningDate}}',
      planningConfirmed: 'Planning confirmed',
      confirmButton: `Confirm planning`,
    },
    refuseOrder: {
      heading: 'Refuse transport order {{order.orderNumber}}',
      orderRefused: 'Order declined',
      orderRefusalError: 'Could not refuse order: {{error}}',
      refuseButton: `Refuse transport order`,
    },
    refusePlanning: {
      heading: 'Refuse planning {{planningDate}}',
      planningRefused: 'Planning declined',
      refuseButton: `Refuse planning`,
    },
    orderSummary: {
      loadAndUnloadInformation: 'Load and unload information',
      load: 'Load',
      unload: 'Unload',
      freightRate: 'Freight rate',
      total: 'Total',
    },
  },
  portal: {
    requestAccount: {
      requestCustomerAccount: 'Request an account as a customer',
      requestCarrierAccount: 'Request an account as a carrier',
      success: 'Request has been sent, we will contact you for further steps.',
      failed: 'Could not request account: {{error}}',
      name: 'Name',
      email: 'Email',
      companyName: 'Company Name',
      companyNumber: 'Company Number', // Or 'Business Registration Number' depending on context
      request: 'Request',
      dontHaveAnAccount: "Don't have an account yet?",
      accountRequestFailed: 'Account request failed',
    },
  },
  customerPortal: {
    switch: 'Switch Customer',
    selectCustomer: 'Select customer',
    requestAccount: {
      title: 'Request an account',
      success: 'Request has been submitted, we will contact you for further steps.',
      failed: 'Could not request account: {{error}}',
      name: 'Name',
      email: 'Email',
      companyName: 'Company Name',
      companyNumber: 'Company Number',
      request: 'Request',
      dontHaveAnAccount: "Don't have an account yet?",
    },
    invite: {
      title: 'Invited to Customer Portal',
      details:
        'You have been invited to the TransDirect customer portal, create your account by entering your password.',
      accepted: 'Invitation accepted, you will be redirected to the homepage shortly.',
      acceptError: 'Could not accept invitation: {{error}}',
      acceptButtonText: 'Accept Invitation',
      password: 'Password',
      repeatPassword: 'Repeat Password',
    },
    home: {
      customerPortal: 'Customer portal',
      title: 'Customer portal',
      hello: 'Hello,',
      welcome: 'Welcome to the customer portal.',
      manageOrders: 'Manage all your transport orders.',
      viewInvoices: 'View and pay invoices easily.',
      createQuotes: 'Create and track quotes.',
      viewLocations: 'View all transport locations.',
    },
    locations: {
      locations: 'Locations',
      location: 'Location',
      name: 'Name',
      street: 'Street',
      number: 'Number',
      countryCode: 'Country Code',
      postalCode: 'Postal Code',
      city: 'City',
      country: 'Country',
      language: 'Language',
      notes: 'Notes',
      createLocation: 'Create Location',
      newLocation: 'New Location',
      locationCreated: 'Location created',
      couldNotCreateLocation: 'Could not create location: {{error}}',
      findLocation: 'Find a location...',
      address: 'Address',
      locationNotFound: 'Location not found',
      couldNotLoadLocation: 'Could not load location: {{error}}',
    },
    order: {
      duplicate: 'Duplicate',
      bulkDuplicate: 'Bulk duplicate',
      bulkDuplicateTitle: 'Bulk duplicate order',
      bulkDuplicateDescription: 'Create multiple copies of this order with incrementing invoice references.',
      numberOfDuplicates: 'Number of duplicates',
      baseInvoiceRef: 'Starting invoice reference',
      baseInvoiceRefHelper: 'Numeric base. Leading zeros are stripped. References become base through base+count-1.',
      skipDraft: 'Skip draft status (create as Created)',
      bulkDuplicateSuccess: '{{count}} orders created',
      bulkDuplicateError: 'Could not duplicate orders: {{error}}',
      referencePreview: 'References: {{first}} through {{last}}',
      overview: 'Overview',
      updateInvoiceRef: 'Update invoice reference',
      updated: 'Order has been updated',
      couldNotUpdate: 'Could not update order: {{error}}',
    },
    orders: {
      orders: 'Orders',
      placeOrder: 'Place an order',
      invoiceRef: 'Invoice reference',
      orderRef: 'Order reference',
      loadLocation: 'Load location',
      unloadLocation: 'Unload location',
      loadDate: 'Load date',
      loadTime: 'Load time',
      unloadDate: 'Unload date',
      unloadTime: 'Unload time',
      invoiceTotal: 'Invoice total',
      status: 'Status',
      invoiceStatus: 'Invoice status',
      hasCMR: 'Has CMR',
      creationDate: 'Creation date',
      executionDate: 'Execution date',
      fileManager: 'File manager',
      cancellationReason: 'Cancellation reason',
      stops: 'Stops',
      deleteStop: 'Delete stop',
      purchases: 'Purchases',
      total: 'Total',
      trailerTypes: 'Trailer types',
      loadingMethod: 'Loading method',
      load: 'Loading',
      unload: 'Unloading',
      typeStop: 'Type stop',
      extraNotes: 'Extra notes',
      noContent: 'No content',
      packaging: 'Packaging',
      coli: 'Coli',
      content: 'Content',
      addContent: 'Add content',
      deleteContent: 'Delete content',
      noStops: 'No stops',
      noStopsError: 'Add at least one loading and unloading stop',
      reference: 'Reference',
      complaints: 'Complaints',
      fileComplaint: 'File a complaint',
      fileComplaintDescription: 'Complete this form to file a complaint',
      send: 'Send',
      reason: 'Reason',
      details: 'Details',
      complaintFiled: 'Complaint filed',
      couldNotFileComplaint: 'Could not file complaint: {{error}}',
      placeAnOrder: 'Place an order',
      assignment: 'Assignment',
      addStop: 'Add stop',
      location: 'Location',
      date: 'Date',
      start: 'Start',
      stop: 'Stop',
      weight: 'Weight (kg)',
      volume: 'Volume (m3)',
      loadMeter: 'Loadmeter',
      stopsRequired: 'At least 1 stop is required',
      contentRequired: 'At least 1 content item is required',
      truck: 'Truck',
      distance: 'Distance',
      emissions: 'Emissions',
      sustainability: 'Sustainability',
      attachments: 'Attachments',
      uploadAttachments: 'Upload attachments',
      attachmentDeleted: 'Attachment deleted',
      couldNotDeleteAttachment: 'Could not delete attachment: {{error}}',
      uploadedAttachment: 'Attachment {{document}} uploaded',
      uploadFailed: 'Could not upload attachment {{document}}: {{error}}',
      route: 'Route',
      chooseMethod: 'Choose a method',
      automatic: 'Automatic',
      automaticDescription:
        'Upload one or more transport order PDFs and our AI will extract the order details automatically. Each PDF becomes a separate order.',
      manual: 'Manual',
      manualDescription: 'Fill in the order form yourself with all the stops, cargo, and delivery details.',
      uploadPdfs: 'Upload transport order PDFs',
      processingPdf: 'Processing PDF...',
      createOrders: 'Create orders',
      orderCreated: 'Order created',
      ordersCreated: '{{count}} orders created',
      couldNotCreateFromPdf: 'Could not create order from PDF: {{error}}',
      uploadAtLeastOnePdf: 'Upload at least one PDF',
      viewOrder: 'View order',
      creatingOrders: 'Creating orders...',
      report: 'Report',
      orderPlaced: 'Order placed',
      couldNotPlaceOrder: 'Could not place order: {{error}}',
      stopNumber: 'Stop {{number}} - {{type}}',
      orderLink: 'Order {{orderNumber}} - {{customerRef}}',
    },
    quotes: {
      quotes: 'Quotes',
      quote: 'Quote',
      quoteReference: 'Quote reference',
      quoteDate: 'Quote date',
      expirationDate: 'Expiration date',
      total: 'Total',
      search: 'Search for a quote...',
      quotation: 'Quote {{reference}}',
      notFound: 'Quote not found',
      departurePlace: 'Departure place',
      destination: 'Destination',
      distance: 'Distance',
      price: 'Price',
      type: 'Type',
      requests: 'Requested quotes',
      request: 'Request a quote',
      requestTitle: 'Quote request {{reference}}',
      notes: 'Notes',
      lines: 'Quote lines',
      addLine: 'Add line',
      noLines: 'No quote lines',
      requestSuccess: 'Quote requested',
      requestFailed: 'Could not request quote: {{error}}',
      requestedAt: 'Requested at',
      quotationRequests: 'Quotation requests',
      hasQuote: 'Has quote',
      generalInformation: 'General information',
      removeLine: 'Remove line',
    },
    quoteLine: {
      from: 'From',
      to: 'To',
    },
    invoices: {
      invoices: 'Invoices',
      invoiceNumber: 'Invoice number',
      invoiceDate: 'Invoice date',
      dueDate: 'Due date',
      total: 'Total',
      search: 'Search for an invoice...',
      notFound: 'Invoice not found',
      invoice: 'Invoice {{invoiceNumber}}',
      structuredComment: 'Structured comment',
      reference: 'Reference',
      sentAt: 'Sent at',
      load: 'Load',
      unload: 'Unload',
      description: 'Description',
      amount: 'Amount',
      unitPrice: 'Unit price',
      vat: 'VAT',
      extraInformation: 'Extra information: {{extraInformation}}',
      orderTotal: 'Order total: €{{total}}',
      totalExclVat: 'Total excl VAT:',
      totalVat: 'Total VAT:',
      totalInclVat: 'Total incl VAT:',
      generalInformation: 'General information',
    },
    creditNotes: {
      creditNotes: 'Credit Notes',
      creditNoteNumber: 'Credit Note Number',
      creditNoteDate: 'Credit Note Date',
      dueDate: 'Due Date',
      total: 'Total',
      search: 'Search for a credit note...',
      notFound: 'Credit Note not found',
      creditNote: 'Credit Note {{creditNoteNumber}}',
      structuredComment: 'Structured Comment',
      reference: 'Reference',
      sentAt: 'Sent on',
      load: 'Load',
      unload: 'Unload',
      description: 'Description',
      amount: 'Amount',
      unitPrice: 'Unit Price',
      vat: 'VAT',
      extraInformation: 'Extra information: {{extraInformation}}',
      orderTotal: 'Order total: €{{total}}',
      totalExclVat: 'Total excl. VAT:',
      totalVat: 'Total VAT:',
      totalInclVat: 'Total incl. VAT:',
      generalInformation: 'General Information',
    },
    businessInformation: {
      businessInformation: 'Business Information',
      updateBusinessInformation: 'Update Business Information',
      name: 'Name',
      vatNumber: 'VAT Number',
      businessNumber: 'Business Number',
      saveChanges: 'Save Changes',
      informationChanged: 'Information has been updated',
      informationNotChanged: 'Could not update information: {{error}}',
      requestChanges: 'To update company name, vat number or business number, please contact us',
    },
    ourInformation: {
      ourInformation: 'Business Information TransDirect',
      name: 'Name',
      vatNumber: 'VAT Number',
      businessNumber: 'Business Number',
      address: 'Address',
      contact: 'Contact',
      phoneNumber: 'Phone Number',
      emailTransport: 'Transport Email',
      emailAccounting: 'Accounting Email',
      emailGeneral: 'General Email',
    },
    contacts: {
      contacts: 'Contacts',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      function: 'Function',
      language: 'Language',
      notifications: 'Contact suitable for',
      newContact: 'New contact',
      contactCreated: 'Contact created',
      couldNotCreateContact: 'Could not create contact: {{error}}',
      createContact: 'Create contact',
      notFound: 'Contact not found',
      updatedContact: 'Contact updated',
      couldNotUpdateContact: 'Could not update contact: {{error}}',
      updateContact: 'Update contact',
      delete: 'Delete',
      deleteContact: 'Delete contact',
      deleteContactConfirmation: 'Are you sure you want to delete contact {{name}}?',
      deleteContactError: 'Could not delete contact: {{error}}',
      deleteContactSuccess: 'Contact deleted',
    },
    users: {
      users: 'Users',
      inviteUser: 'Invite User',
      invitedUsers: 'Invited Users',
      noInvitedUsers: 'No invited users',
      removeInvite: 'Remove Invitation',
      confirmRemoveInvite: 'Are you sure you want to remove the invitation for {{email}}?',
      inviteRemoved: 'Invitation removed',
      couldNotRemoveInvite: 'Could not remove invitation: {{error}}',
      noUsersFound: 'No users found',
      removeUser: 'Remove User',
      confirmRemoveUser: 'Are you sure you want to remove the user {{name}}?',
      userRemoved: 'User removed',
      couldNotRemoveUser: 'Could not remove user: {{error}}',
      active: 'Active',
      inactive: 'Inactive',
      userInvited: 'User invited',
      couldNotInviteUser: 'Could not invite user: {{error}}',
      name: 'Name',
      email: 'Email',
      isActive: 'Is active?',
      invited: 'Invited',
    },
    cmrReminder: {
      cmrReminder: 'CMR Reminder',
      sendReminder: 'Send',
      confirmText: 'Are you sure you want to send a CMR reminder?',
      confirmation: 'CMR Reminder sent',
      error: 'Could not send CMR reminder: {{error}}',
    },
    sustainability: {
      sustainability: 'Sustainability',
      policy: 'Sustainability Policy',
      viewOverview: 'View sustainability overview',
      orderCount: 'Number of orders',
      totalDistance: 'Total distance',
      totalWeight: 'Total weight',
      totalEmissions: 'Total CO2 emissions',
      averageDistance: 'Average distance',
      averageWeight: 'Average weight',
      averageEmissions: 'Average CO2 emissions',
      total: 'Total',
      avgPerOrder: 'Average per order',
    },
    settings: {
      settings: 'Settings',
    },
    apiKeys: {
      apiKeys: 'API Keys',
      createApiKey: 'Create API Key',
      name: 'Name',
      scopes: 'Permissions',
      status: 'Status',
      lastUsed: 'Last used',
      createdAt: 'Created at',
      prefix: 'Prefix',
      active: 'Active',
      revoked: 'Revoked',
      expired: 'Expired',
      never: 'Never',
      revokeApiKey: 'Revoke API Key',
      confirmRevoke: 'Are you sure you want to revoke API key "{{name}}"? This cannot be undone.',
      apiKeyRevoked: 'API key has been revoked',
      couldNotRevoke: 'Could not revoke API key: {{error}}',
      apiKeyCreated: 'API key has been created',
      couldNotCreate: 'Could not create API key: {{error}}',
      copyKey: 'Copy key',
      keyCopied: 'Key copied',
      keyWarning: 'Store this key securely. It will not be shown again.',
      noApiKeys: 'No API keys found',
      ordersRead: 'Read orders',
      ordersWrite: 'Create orders',
      maxKeysReached: 'Maximum number of API keys reached',
      scopeRequired: 'At least one scope is required',
    },
    loading: 'Loading...',
  },
  liveTracking: {
    title: 'Tracking',
    subtitle: 'Location and on-board computer of this truck.',
    loading: 'Fetching location…',
    noPosition: 'No location known for this truck yet.',
    noPositionHint: 'Link a vehicle to this licence plate on the integrations page.',
    noConnection: 'No tracking connection follows this truck yet. Connect one to see its location.',
    driving: 'Driving',
    idle: 'Parked',
    location: 'Location',
    speed: 'Speed',
    heading: 'Heading',
    odometer: 'Odometer',
    fuel: 'Fuel',
    lastMeasurement: 'Last measurement',
    liveActive: 'Live tracking on — refreshing every 30 seconds.',
    lastSignal: 'Last signal',
    trailer: 'Trailer',
    refresh: 'Refresh',
    startLive: 'Start live tracking',
    stopLive: 'Stop live tracking',
    resizePanel: 'Resize side panel',
    connections: 'Tracking connections',
    connectionsSubtitle: 'Connect a supplier’s tracking environment to follow their trucks.',
    addConnection: 'Add tracking connection',
    integrations: 'Tracking integrations',
    integrationsHint: 'Pick a provider to connect a supplier’s tracking.',
    providerFleetmaster: 'Follow the location and onboard computer of trucks via FleetMaster.lt.',
    connectionLabel: 'Name',
    connectionLabelHelp: 'How this connection appears in the list.',
    helpCompanyId: 'The company ID from your FleetMaster telematics account.',
    helpSuivoBaseUrl: 'Leave empty for the standard Suivo environment.',
    ownerLabel: 'Supplier ID',
    ownerHelp: 'For a connection belonging to a charter/supplier.',
    credentialsNote: 'The key is stored encrypted and is never shown again after saving.',
    connect: 'Connect',
    back: 'Back',
    noConnections: 'No tracking connection yet.',
    noConnectionsHint: 'Add one to see where the trucks are.',
    moreConnectionsTitle: 'More tracking connections are coming soon',
    moreConnectionsBody: 'We are working on more tracking connections — new providers appear here automatically.',
    connectionsLoading: 'Fetching connections…',
    vehiclesLoading: 'Fetching vehicles…',
    noVehicles: 'This connection reports no vehicles yet.',
    statusActive: 'Active',
    statusError: 'Error',
    keyLabel: 'key',
    linkedCount: '{{linked}} of {{total}} vehicles linked',
    lastChecked: 'last check',
    never: 'never',
    supplierOwner: 'Supplier #{{id}}',
    connectionSettings: 'Connection settings',
    verify: 'Check',
    verifyOk: 'The connection works.',
    verifyFailed: 'The connection could not be checked.',
    remove: 'Delete',
    removeTitle: 'Delete connection',
    removeDescription: 'The connection "{{label}}" and the stored key will be deleted.',
    connectionRemoved: 'Connection deleted.',
    linkVehicles: 'Link vehicles',
    linkVehiclesHint: 'Pick a vehicle, then the truck it drives in.',
    link: 'Link',
    unlink: 'Unlink',
    linkTruck: 'Link truck',
    chooseTruck: 'Truck',
    chooseTruckHelp: 'Choose the truck this vehicle drives in.',
    chooseTruckRequired: 'Choose a truck',
    moreTrucks: 'Only the first {{count}} trucks are shown — search to narrow the list.',
    plateMatch: 'licence plate matches',
    linkedTo: 'Linked to {{truck}}',
    linkedManually: 'manual',
    notLinked: 'Not linked',
    vehicleLinked: 'Vehicle linked.',
    vehicleUnlinked: 'Link removed.',
    trailerSubtitle: 'Location of this trailer.',
    linkTracker: 'Link tracker',
    linkTrackerHint: 'Pick the vehicle that sits in this trailer.',
    linkTrailer: 'Link trailer',
    linkTrailerHint: 'Pick a vehicle, then the trailer it sits on.',
    chooseTrailer: 'Trailer',
    chooseTrailerRequired: 'Pick a trailer',
    noTrackerLinked: 'No tracker is linked to this trailer yet — link one to see its location.',
    noTrailerPosition: 'No location known for this trailer yet.',
    searchUnits: 'Search by name or plate…',
    noUnits: 'No vehicles reported yet.',
    providerSuivo: 'Track the location of our trailers with Suivo trackers.',
    ownerKindLabel: 'Owner',
    ownerKindCompany: 'Own fleet (company)',
    ownerKindCarrier: 'Carrier/supplier',
    supplierOwnerCompany: 'Own fleet',
    status: {
      moving: 'Moving',
      parked: 'Parked',
      idling: 'Idling',
      immobilized: 'Immobilised',
      offline: 'No signal',
      inOperation: 'In operation',
    },
    trailersMap: {
      searchLabel: 'Search (TDR ID / name / plate)',
      searchPlaceholder: 'Search by name or plate...',
      trackerLabel: 'Tracker',
      trackerAll: 'All trailers',
      trackerTracked: 'Tracked',
      trackerUntracked: 'Untracked',
      trackerSilent: 'No signal > 24h',
      tableCardTitle: 'Trailers',
      mapCardTitle: 'Map',
      tableTrailer: 'Trailer',
      tablePlate: 'Plate',
      tableTracker: 'Tracker',
      tableStatus: 'Status',
      tableLastSignal: 'Last signal',
      tablePlace: 'Place',
      noTracker: 'No tracker',
      clearFilters: 'Clear all filters',
      noneTrackedTitle: 'No tracker linked yet',
      noneTrackedDescription: 'Link a Suivo unit to a trailer to see it on the map.',
      noneInFilter: 'No trailers in this selection',
      noConnectionsTitle: 'No tracking connection yet',
      noConnectionsDescription: 'Add a connection to see where our trailers are.',
      noConnectionsAction: 'Go to tracking integrations',
      connectionErrorTitle: 'Tracking connection with an error',
    },
  },
  carrierPortal: {
    liveTracking: { liveTracking: 'Tracking' },
    selectCarrier: 'Select carrier',
    loading: 'Loading...',
    switch: 'Switch Carrier',
    home: {
      carrierPortal: 'Carrier Portal',
      title: 'Carrier Portal',
      hello: 'Hello,',
      welcome: 'Welcome to the carrier portal.',
      manageOrders: 'Manage all your transport orders.',
      managePlanning: 'Manage your planning.',
      selfBilling: 'Create and manage your self-billing invoices.',
      info: 'Information',
      infoSubtitle: 'Information about TransDirect & the carrier portal',
      contact: 'Contact Person',
      contactSubtitle: 'Who is your TransDirect contact person',
      FuelCard: 'Fuel Card',
      FuelCardSubtitle: 'Request your personalized TransDirect fuel card',
      saveChanges: 'Save changes',
    },
    orders: {
      allAccepted: 'All orders confirmed',
      customerReference: 'Customer reference',
      draft: 'Draft',
      load: 'Load',
      orders: 'Orders',
      manageOrders: 'Manage all your transports & upload your transport documents',
      orderRef: 'TD-Reference',
      loadLocation: 'Loading Location',
      unload: 'Unload',
      unloadLocation: 'Unloading Location',
      invoiceTotal: 'Freight Rate',
      hasCMR: 'Has documents',
      documents: 'Documents',
      cmrRejected: 'Refused',
      cmrPending: 'In progress',
      cmrUploadHint: 'You can upload one or more PDF files; we will merge them into a single document.',
      cmrUploaded: 'Document uploaded',
      cmrUploadFailed: 'Could not upload document: {{error}}',
      documentValidation: 'Documents',
      documentValidationNoDocuments: 'No documents',
      documentValidationPending: 'In progress',
      documentValidationApproved: 'Approved',
      documentValidationRejected: 'Rejected',
      outstandingDocsTitle: 'Documents still required',
      requiredDocMissing: 'missing',
      requiredDocInReview: 'under review',
      requiredDocRejected: 'rejected — please re-upload',
      requiredDocStatusReceived: 'Received',
      requiredDocStatusInReview: 'In review',
      requiredDocStatusRejected: 'Rejected',
      requiredDocStatusMissing: 'Missing',
      requiredDocsTitle: 'Required documents',
      requiredDocsSubtitle: 'This customer requires the documents below for this order.',
      requiredDocsProgress: '{{done}} of {{total}} received',
      requiredDocReupload: 'Upload again',
      uploadDocumentTitle: 'Upload {{document}}',
      uploadRequiredDoc: 'Upload',
      uploadDocumentsTitle: 'Upload documents',
      uploadDocumentsHint:
        'Upload one or more files at once; we split them and file each part under the right document type.',
      docReasonInReview: 'being checked',
      docReasonRejected: 'rejected',
      docRejectionLowQuality: 'unreadable',
      docRejectionMissingSignature: 'signature missing',
      docRejectionNoOrderReference: 'no reference found',
      docRejectionNotNeutral: 'CMR is not neutral',
      docRejectionWrongCustomer: 'wrong customer',
      docRejectionWrongDeliveryAddress: 'wrong delivery address',
      docRejectionWrongDocumentType: 'wrong document type',
      docRejectionWrongOrgOnDocument: 'wrong company',
      requiredDocUploaded: 'Document uploaded',
      requiredDocUploadFailed: 'Document could not be uploaded: {{error}}',
      purchases: 'Freight Rate',
      reference: 'Reference',
      assignee: 'Assigned to',
      orderAssigned: 'Order assigned',
      changeAssignee: 'Change',
      assignOrder: 'Assign Order',
      assign: 'Assign',
      route: 'Route',
      error: 'Error',
      report: 'Report',
    },
    planning: {
      acceptAll: 'Accept all',
      actions: 'Actions',
      date: 'Date',
      loadingOrders: 'Loading orders...',
      noTruck: 'This carrier does not have a truck yet.',
      ordersForTruck: 'Orders for truck with license plate {{licensePlate}}',
      planning: 'Planning',
      truck: 'Truck',
      managePlanning: 'View and manage your transport planning',
    },
    selfBilling: {
      selfBilling: 'Self Billing',
      rideOverview: 'Ride overview',
      manageRideOverview: 'View and download your ride overviews',
      searchRideOverviewPlaceholder: 'Search a ride overview...',
      type: 'Type',
      number: 'Number',
      manageSelfBilling: 'View and download your Self Billing',
      searchPlaceholder: 'Search a self billing...',
      id: 'Reference',
      date: 'Date',
      download: 'Download',
      invoice: 'Invoice',
      title: 'Self Billing Invoice',
    },
    generalInformation: {
      generalInformation: 'Information',
      noDocumentsAvailable: 'No documents available',
      checkBackLater: 'Check back later for updated documentation',
    },

    businessInformation: {
      businessInformation: 'Business Information',
      settingsAndBusinessInformation: 'Settings and Business Information',
      tabGeneral: 'General',
      tabFleet: 'Fleet',
      tabContacts: 'Contacts',
      civilLiabilityInsurance: 'Civil Liability Insurance',
      CMRInsurance: 'CMR Insurance',
      transportationInsurance: 'Transportation Insurance',
      documentUploaded: 'Document uploaded',
      insurances: 'Insurances',
    },
    contacts: {
      contacts: 'Contacts',
      newContact: 'New Contact',
      updateContact: 'Save Contact',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      function: 'Function', // Or 'Role' could be used depending on context
      language: 'Language',
      delete: 'Delete',
      createContactSuccess: 'Contact created successfully',
      createContactError: 'Error creating contact: {{error}}',
      updateContactSuccess: 'Contact updated successfully',
      updateContactError: 'Error updating contact: {{error}}',
      deleteContact: 'Delete Contact',
      deleteContactConfirmation: 'Are you sure you want to delete your contact?',
      deleteContactSuccess: 'Contact deleted successfully',
      deleteContactError: 'Error deleting contact: {{error}}',
      notFound: 'Contact not found',
      notifications: 'Notifications',
    },
    trucks: {
      title: 'Trucks',
      newTruck: 'New Truck',
      update: 'Update Truck',
      licensePlate: 'License Plate',
      codeName: 'Code Name',
      planner: 'Planner',
      isActive: 'Active',
      insuranceExpiresAt: 'Insurance expires on',
      examinationExpiresAt: 'Inspection expires on',
      documents: 'Documents',
      yes: 'Yes',
      no: 'No',
      moveUp: 'Move Up',
      moveDown: 'Move Down',
      create: 'Create',
      succeeded: 'Truck saved',
      createError: 'Could not create truck: ',
      updateError: 'Could not update truck: ',
      insurance: 'Insurance',
      insuranceUploaded: 'Insurance saved',
      examination: 'Inspection',
      examinationUploaded: 'Inspection saved',
      notFound: 'No trucks found',
      appLoginTitle: 'Driver app login',
      appLoginHint:
        'Drivers sign in with the license plate. Set the password here; drivers cannot reset it themselves.',
      appPasswordLabel: 'Driver app password',
      appLoginLabel: 'Login (license plate)',
      appCodeNameLabel: 'Codename',
      appSavePassword: 'Save password',
      appDisableLogin: 'Disable login',
      appPasswordSaved: 'Password saved',
      appLoginDisabled: 'Driver app login disabled',
      detailsTitle: 'Tractor details',
      documentCount: '{{count}} of {{total}}',
      statusValid: 'Valid',
      statusExpiringSoon: 'Expiring soon',
      statusExpired: 'Expired',
      statusMissing: 'Missing',
      expiresOn: 'Expires {{date}}',
      noDocument: 'No document uploaded yet',
      viewDocument: 'View',
      replaceDocument: 'Replace',
      uploadDocument: 'Upload',
      searchPlaceholder: 'Search by licence plate…',
      noExpiryKnown: 'No expiry date known',
      notUploadedYet: 'Not uploaded yet',
    },
    trailers: {
      title: 'Trailers',
      truckAmount: 'Number of trucks',
      tautlinerAmount: 'Number of tautliners',
      boxTrailerAmount: 'Number of box/frigo trailers',
      openTrailerAmount: 'Number of open trailers',
      save: 'Save changes',
      saveSuccess: 'Carrier updated',
      saveError: 'Could not update carrier: {{error}}',
      subtitle: 'Amounts per type — used for capacity planning',
    },
    driverBlock: {
      title: 'Driver access',
      message: 'Please log in to the driver app on your mobile to view your orders.',
    },
    notFound: {
      pageNotFound: 'Page not found.',
      pageNotFoundDesc:
        'This page does not seem to exist (yet). If you think something is wrong, please contact your TransDirect contact person.',
    },
    invite: {
      title: 'Invited to Carrier Portal',
      details:
        'You have been invited to the TransDirect carrier portal, create your account by entering your password.',
      accepted: 'Invitation accepted, you will be redirected to the homepage shortly.',
      acceptError: 'Could not accept invitation: {{error}}',
      acceptButtonText: 'Accept Invitation',
      password: 'Password',
      repeatPassword: 'Repeat Password',
    },
    users: {
      users: 'Users',
      inviteUser: 'Invite User',
      invitedUsers: 'Invited Users',
      noInvitedUsers: 'No invited users',
      removeInvite: 'Remove Invitation',
      confirmRemoveInvite: 'Are you sure you want to remove the invitation for {{email}}?',
      inviteRemoved: 'Invitation removed',
      couldNotRemoveInvite: 'Could not remove invitation: {{error}}',
      noUsersFound: 'No users found',
      removeUser: 'Remove User',
      confirmRemoveUser: 'Are you sure you want to remove the user {{name}}?',
      userRemoved: 'User removed',
      couldNotRemoveUser: 'Could not remove user: {{error}}',
      active: 'Active',
      inactive: 'Inactive',
      userInvited: 'User invited',
      couldNotInviteUser: 'Could not invite user: {{error}}',
      name: 'Name',
      email: 'Email',
      isActive: 'Is active?',
      role: 'Role',
      roleDispatcher: 'Dispatcher',
      roleDriver: 'Driver',
      activeUsers: 'Active Users',
      pendingInvitations: 'Pending Invitations',
      sendInvitation: 'Send Invitation',
      sending: 'Sending...',
      cancel: 'Cancel',
      emailAddress: 'Email Address',
      deleteInvitation: 'Delete Invitation',
      joined: 'Joined',
      invited: 'Invited',
      assignee: 'Assignee',
      assignOrder: 'Assign Order',
      unassigned: 'Unassigned',
      assignedTo: 'Assigned to',
      roleUpdated: 'Role updated',
      renameUser: 'Rename User',
      newName: 'New name',
      userRenamed: 'User renamed',
      save: 'Save',
      permissions: 'Permissions',
      canRenameUsers: 'Can rename users',
      permissionsUpdated: 'Permissions updated',
      nameOptional: 'Name (optional)',
      emailRequired: 'Please enter an email address',
      namePlaceholder: 'John Doe',
      emailPlaceholder: 'user@example.com',
    },
    tracking: {
      loading: 'Loading tracking events...',
      events: 'Tracking Events',
      noEvents: 'No tracking events found',
      scheduled: 'Scheduled',
      eventTypes: {
        StatusLoaded: 'Loaded',
        StatusUnloaded: 'Unloaded',
        LocationPing: 'Location update',
      },
      stop: 'Stop',
      by: 'by',
    },
  },
  carrierContactNotifications: {
    transportOrders: 'Transport Orders',
    selfBillings: 'Self Billings',
  },
  orderStatus: {
    Draft: 'Draft',
    Created: 'Created',
    Prepayment: 'Prepayment',
    Placed: 'Placed',
    Confirmed: 'Confirmed',
    Refused: 'Refused',
    Executed: 'Executed',
    Cancelled: 'Cancelled',
  },
  orderSaleStatus: {
    Created: 'Created',
    Credited: 'Credited',
    Incomplete: 'Incomplete',
    Invoiced: 'Invoiced',
  },
  customerContactNotifications: {
    invoice: 'Invoice',
    orderConfirmation: 'Order Confirmation',
    cmr: 'CMR',
    quotation: 'Quotation',
    dieselSurcharge: 'Diesel Surcharge',
    paymentReminder: 'Payment Reminder',
  },
  errors: {
    required: 'Required',
    invalidEmail: 'Invalid email',
    dateShouldBeInTheFuture: 'Date should be in the future',
    startTimeShouldBeBeforeEndTime: 'Start time should fall before end time',
    passwordsDontMatch: 'Password and repeat password do not match.',
    passwordTooShort: 'Password is too short.',
    formValidation: 'Please check the highlighted fields in the form',
    retry: 'Try again',
    reloadPage: 'Reload page',
    networkTitle: 'Connection problem',
    networkDescription: 'We cannot reach the server. Check your internet connection and try again.',
    serverTitle: 'Server error',
    serverDescription: 'The server could not process your request. Please try again later.',
    notFoundTitle: 'Not found',
    notFoundDescription: 'The requested data no longer exists or is unavailable.',
    sessionTitle: 'Session expired',
    sessionDescription: 'Your session has expired or you are not logged in. Log in again to continue.',
    permissionTitle: 'No access',
    permissionDescription: 'You do not have access to this data.',
    validationTitle: 'Invalid data',
    validationDescription: 'Please check the entered data and try again.',
    rateLimitTitle: 'Too many requests',
    rateLimitDescription: 'Too many requests were sent. Wait a moment and try again.',
    abortedTitle: 'Request cancelled',
    abortedDescription: 'The request was cancelled. Try again if needed.',
    unknownTitle: 'Unknown error',
    unknownDescription: 'An unexpected error occurred. Try again or contact support if this keeps happening.',
    loadingTitle: 'Loading failed',
    loadingDescription: 'The data could not be loaded.',
  },
  orderComplaintType: {
    Aggression: 'Physical/Verbal aggression',
    DamagedGoods: 'Damaged goods',
    IncorrectDelivery: 'Incorrect delivery',
    IncorrectInvoice: 'Incorrect invoice',
    LateDelivery: 'Late delivery',
    Other: 'Other',
  },
  myProfile: {
    myProfile: 'My Profile',
    logout: 'Logout',
    name: 'Name',
    role: 'Role',
    email: 'Email',
    language: 'Language',
    languageChanged: 'Language updated',
    languageNotChanged: 'Could not update language: {{error}}',
    joinedAt: 'Joined at',
    nameChanged: 'Name has been updated',
    nameNotChanged: 'Could not update name: {{error}}',
  },
  auth: {
    forgotPassword: {
      title: 'Forgot Password',
      requestNewPassword: 'Request New Password',
      instructions:
        'Enter your email, if the user exists in the system, you will soon receive an email to set a new password.',
      requestFailed: 'Could not request a new password',
      instructionsSent: 'Instructions to set a new password have been sent to your email address.',
    },
    configNewPassword: {
      title: 'Set New Password',
      newPassword: 'New Password',
      repeatNewPassword: 'Repeat New Password',
      confirmButtonText: 'Set New Password',
      passwordDontMatch: 'Password and repeat password must match',
      tokenNotFound: 'Token not found',
      invalidToken: 'Token is no longer valid',
      configFailed: 'Could not set password: {{error}}',
    },
    login: {
      login: 'Login',
      email: 'Email',
      password: 'Password',
      forgotPassword: 'Forgot Password',
      customerLoginTitle: 'Customer Login',
      loginWithGoogle: 'Login with Google',
      incorrectCredentials: 'Email or password is incorrect',
      loginFailed: 'Could not log in: {{error}}',
    },
    userNotFound: {
      title: 'User Not Found',
      description: 'Could not log in, an error occurred, or the user no longer exists.',
      goToLoginPage: 'Go to Login Page',
    },
  },
  upload: {
    title: 'Upload',
    dropzoneHint: 'Click here or drag file(s) into the box to upload file(s)',
    uploadDocuments: 'Upload documents',
    confirmDeleteDocument: 'Are you sure you want to delete this document?',
    deleteDocument: 'Delete document',
    noDocumentsFound: 'No documents found',
    noDocumentsSelected: 'No documents selected',
    uploadFailed: 'Could not upload document',
    uploadDocumentFailed: 'Could not upload file {{filename}}: {{reason}}',
    multiUploadFailed: 'Could not upload files: {{reason}}',
  },
  or: 'or',
  draft: 'Draft',
  cancel: 'Cancel',
  close: 'Close',
  moreInfo: 'More info',
  previous: 'Previous',
  next: 'Next',
  delete: 'Delete',
  save: 'Save',
  download: {
    open: 'Open',
    download: 'Download',
    started: 'Download started',
    failed: 'Download failed: {{message}}',
  },
  chat: {
    title: 'Chat',
    sendMessage: 'Send message',
    placeholder: 'Type a message...',
    noMessages: 'No messages yet',
    loadMore: 'Load more messages',
    messageSent: 'Message sent',
    messageError: 'Could not send message: {{error}}',
    newMessage: '{{name}}: new message on order {{orderNumber}}',
    internal: 'Internal',
    carrier: 'Carrier',
  },
};
